import type { ReactElement } from "react"
import { useContext } from "react"
import LinkAtm from "@onestore/hel/dist/components/atoms/LinkAtm"
import type Theme from "@onestore/hel/dist/typings/theme"
import CtaModal from "@gatsby-plugin-generic-page/components/CtaModal"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import { getCtaLinkFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { replaceLinkValues } from "@gatsby-plugin-generic-page/helpers/linkParser"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import SearchUrlParametersContext from "~/context/SearchUrlParametersContext"
import useModalState from "~/hooks/useModalState"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import FormLink from "../FormLink"

interface Props {
  link: CtaElement
  typography?: Theme.Typography
}

function Link({ link, typography }: Props): ReactElement | null {
  const { currentPriceType } = usePriceTypeContext()
  const {
    actionTypeName,
    text,
    linkType,
    title,
    gaEvent,
    anchorId,
    form,
    href,
    openInNewTab,
    pages,
    modalTitle,
    texts,
  } = getCtaLinkFlatData(link, currentPriceType)
  const { isOpen, open, close } = useModalState(gaEvent)
  const currentSearchUrlParameters = useContext(SearchUrlParametersContext)

  if (isEmpty(actionTypeName)) {
    return null
  }

  const commonProps = {
    typography,
    title,
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.SCROLL_TO_SECTION) &&
    !isEmpty(anchorId)
  ) {
    return (
      <LinkAtm
        {...commonProps}
        scrollTo={anchorId}
        onClick={() => sendGaEvent(gaEvent)}
      >
        {text}
      </LinkAtm>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.OPEN_FORM) &&
    !isEmpty(form)
  ) {
    return (
      <FormLink {...commonProps} text={text} form={form} linkType={linkType} />
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.OPEN_MODAL) &&
    !isEmpty(texts)
  ) {
    return (
      <>
        <LinkAtm {...commonProps} onClick={open}>
          {text}
        </LinkAtm>

        <CtaModal
          isOpen={isOpen}
          texts={texts}
          title={modalTitle}
          onCloseButtonClick={close}
        />
      </>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.INTERNAL_LINK) &&
    !isEmpty(pages)
  ) {
    return (
      <LinkAtm
        {...commonProps}
        onClick={() => sendGaEvent(gaEvent)}
        to={pages[0].flatData.url}
      >
        {text}
      </LinkAtm>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.EXTERNAL_LINK) &&
    !isEmpty(href)
  ) {
    return (
      <LinkAtm
        {...commonProps}
        onClick={() => sendGaEvent(gaEvent)}
        href={replaceLinkValues(href, currentSearchUrlParameters)}
        openInNewTab={openInNewTab}
      >
        {text}
      </LinkAtm>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.CTA_AX_LINK) &&
    !isEmpty(href)
  ) {
    return (
      <LinkAtm
        {...commonProps}
        onClick={() => sendGaEvent(gaEvent)}
        href={replaceLinkValues(href, currentSearchUrlParameters)}
      >
        {text}
      </LinkAtm>
    )
  }

  return null
}

export default Link
